import { SourceWirePaymentDetails } from '@/bundle/shared/components/SourceWirePaymentDetails/SourceWirePaymentDetails';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { Button } from '@/components/Button/Button';
import { AssignedWireType } from '@/types/wireTypes';
import { isRecipientProxy } from '@/helpers/wireHelpers';
import { ConfirmModal, ConfirmModalHint } from '@/components/ConfirmModal/ConfirmModal';
import { EmailLink } from '@/components/EmailLink/EmailLink';
import { useState } from 'react';
import { ProvidePaymentDetailsTimer } from '../ProvidePaymentDetailsTimer/ProvidePaymentDetailsTimer';
import { TrustStampResponseStatusType } from '@/api/v1/recipient/getRecipientTrustStampStatusApi';
import { isTrustStampSucceedStatus } from '../RecipientDocumentVerification/helpers';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { SourceWireButtonsContainer } from './styles';
import { Box } from '@/components/Box/Box';
import { FormikTextArea } from '@/components/form/fields/FormikTextArea/FormikTextArea';
import { useFormik } from 'formik';
import { CONFIRMATION_WIRE_NOTES_INITIAL_VALUES, CONFIRMATION_WIRE_NOTES_VALIDATION_SCHEMA } from './const';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { SourceWireDivider } from '@/bundle/shared/components/SourceWirePaymentDetails/styles';
import { formatNotes } from '@/helpers/formatHelpers';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { confirmPaymentDetails, loadRecipientSourceWirePaymentDetails } from '@/bundle/_Recipient/_Details/api/api';

type RecipientSourceWirePaymentDetailsType = {
  wire: AssignedWireType;
  onProvidePaymentDetails: () => void;
  trustStampDetails: TrustStampResponseStatusType;
  onResetTrustStampNextAttemptTimer: () => void;
};

type ConfirmationWireNotesFormValues = {
  notes?: string;
};

export const RecipientSourceWirePaymentDetails = ({
  wire,
  onProvidePaymentDetails,
  trustStampDetails,
  onResetTrustStampNextAttemptTimer,
}: RecipientSourceWirePaymentDetailsType) => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);

  const isProxy = isRecipientProxy(wire);
  const wireId = wire?.id;
  const createdByEmail = wire?.created_by?.email;

  const formik = useFormik<ConfirmationWireNotesFormValues>({
    initialValues: CONFIRMATION_WIRE_NOTES_INITIAL_VALUES,
    validationSchema: CONFIRMATION_WIRE_NOTES_VALIDATION_SCHEMA,
    onSubmit: () => {},
  });

  const { data: paymentDetailsData } = useQuery({
    queryKey: ['load_source_wire_payment_details', wireId],
    queryFn: () => loadRecipientSourceWirePaymentDetails(wireId),
    enabled: !!wireId && !isProxy,
  });

  const { mutate, data, isPending } = useMutation({
    mutationKey: ['confirm_payment_details'],
    mutationFn: (values: ConfirmationWireNotesFormValues) => {
      if (isPending) return;

      const payload = {
        notes: formatNotes(values?.notes || ''),
      };

      return confirmPaymentDetails(id, payload);
    },
    onSuccess(response) {
      if (response?.error) {
        return showSnackbarResponseError(response?.error);
      }

      amplitudeService.logEvent(AMPLITUDE_EVENTS.OutboundWireSourceWireConfirmSuccess);
      setIsOpenConfirmModal(false);
      queryClient.invalidateQueries({ queryKey: ['load_recipient_wire', id] });
    },
  });

  const openConfirmModal = () => {
    if (!formik.isValid) return;

    amplitudeService.logEvent(AMPLITUDE_EVENTS.OutboundWireSourceWireConfirmRedirect);
    setIsOpenConfirmModal(true);
  };

  const closeConfirmModal = () => {
    setIsOpenConfirmModal(false);
  };

  const paymentDetails = paymentDetailsData?.body;
  const isDocumentVerificationRequired = !!wire?.is_pd_provider_kyc_required;
  const expiredPaymentDetailsTime = trustStampDetails?.provide_pd_disable_after_sec;
  const isDisableButton = expiredPaymentDetailsTime === 0 && isTrustStampSucceedStatus(trustStampDetails?.status);
  const apiError = data?.error;

  if (!paymentDetails) return null;

  return (
    <>
      <SourceWirePaymentDetails
        wire={wire}
        paymentDetails={paymentDetails}
        notification='Please confirm your wire info.'
        actionSlot={
          isDocumentVerificationRequired && (
            <ProvidePaymentDetailsTimer
              expiredPaymentDetailsTime={expiredPaymentDetailsTime}
              onResetProvidePaymentDetailsTimer={onResetTrustStampNextAttemptTimer}
            />
          )
        }
      >
        <Box>
          <SourceWireDivider />
          <FormikForm value={formik}>
            <FormikTextArea name='notes' label='Notes' placeholder='Enter Notes' apiError={apiError} />
          </FormikForm>
          <SourceWireButtonsContainer>
            <Button variant='secondary' onClick={onProvidePaymentDetails} disabled={isDisableButton}>
              Provide New
            </Button>
            <Button onClick={openConfirmModal} disabled={isDisableButton}>
              Confirm
            </Button>
          </SourceWireButtonsContainer>
        </Box>
      </SourceWirePaymentDetails>

      <ConfirmModal
        isOpen={isOpenConfirmModal}
        header='Confirm Wire Info'
        body={
          <>
            <ConfirmModalHint>You are certifying the validity of the wire info you have provided.</ConfirmModalHint>
            <ConfirmModalHint>
              This information will be committed to the WireVault blockchain and cannot be changed.
            </ConfirmModalHint>
            <ConfirmModalHint>
              If you need to make a change to your wire info, please contact{' '}
              <EmailLink email={createdByEmail} variant='big' /> to request a cancellation before the wire is sent.
            </ConfirmModalHint>
          </>
        }
        onClose={closeConfirmModal}
        onConfirm={() => mutate(formik.values)}
        isLoading={isPending}
      />
    </>
  );
};
